/**
 * @fileoverview Label component - Atomic UI element
 * 
 * A flexible label component for form fields and UI elements.
 * Built with Tailwind CSS and CVA for variant management.
 * 
 * @module ui/shared/atoms/Label
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Label variant definitions using CVA
 */
const labelVariants = cva(
  "font-medium text-gray-700 block",
  {
    variants: {
      size: {
        sm: "text-xs",
        default: "text-sm",
        lg: "text-base",
      },
      required: {
        true: "after:content-['*'] after:ml-0.5 after:text-red-500",
        false: "",
      },
    },
    defaultVariants: {
      size: "default",
      required: false,
    },
  }
);

/**
 * Label component props
 */
export interface LabelProps
  extends React.LabelHTMLAttributes<HTMLLabelElement>,
    VariantProps<typeof labelVariants> {
  /**
   * Whether the field is required (shows asterisk)
   */
  required?: boolean;
}

/**
 * Label component
 * 
 * A flexible label component for form fields.
 * 
 * @param {LabelProps} props - Label props
 * @returns {JSX.Element} Label element
 * 
 * @example
 * <Label htmlFor="email" required>Email Address</Label>
 * 
 * @example
 * <Label size="lg" required={false}>Optional Field</Label>
 */
const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, size, required, ...props }, ref) => {
    return (
      <label
        ref={ref}
        className={cn(labelVariants({ size, required }), className)}
        {...props}
      />
    );
  }
);

Label.displayName = "Label";

export { Label, labelVariants };

